import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";

const FIELDS_BY_TAB = {
    listings: [
        { name: "title", type: "text" },
        { name: "author", type: "text" },
        { name: "price", type: "number", step: "0.01" },
        { name: "currency", type: "select", options: ["EUR", "USD", "GBP"] },
        { name: "condition", type: "select", options: ["new", "like_new", "very_good", "good", "acceptable"] },
        { name: "status", type: "select", options: ["available", "reserved", "sold", "removed"] },
        { name: "isbn", type: "text" },
        { name: "cover_url", type: "text" },
        { name: "description", type: "textarea" },
    ],
    clubs: [
        { name: "name", type: "text" },
        { name: "slug", type: "text" },
        { name: "kind", type: "select", options: ["official", "community"] },
        { name: "language", type: "select", options: ["es", "en"] },
        { name: "theme", type: "text" },
        { name: "description", type: "textarea" },
    ],
};

export default function AdminEditModal({ tab, item, onClose, onSaved }) {
    const { t } = useTranslation();
    const fields = FIELDS_BY_TAB[tab] || [];
    const [form, setForm] = useState(() =>
        Object.fromEntries(fields.map((f) => [f.name, item?.[f.name] ?? ""])),
    );
    const [saving, setSaving] = useState(false);

    // Close on Escape.
    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose?.();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

    const submit = async (e) => {
        e.preventDefault();
        const payload = {};
        for (const f of fields) {
            const original = item?.[f.name] ?? "";
            const next = form[f.name] ?? "";
            if (String(next) === String(original)) continue;
            if (f.type === "number") {
                const num = Number(next);
                if (Number.isFinite(num)) payload[f.name] = num;
            } else {
                payload[f.name] = next === "" ? null : next;
            }
        }
        if (Object.keys(payload).length === 0) {
            toast.message(t("admin_edit.no_changes"));
            onClose?.();
            return;
        }
        const endpoint = tab === "listings"
            ? `/admin/listings/${item.id}`
            : `/admin/clubs/${item.id}`;
        setSaving(true);
        try {
            const { data } = await api.patch(endpoint, payload);
            toast.success(t("admin_edit.saved"));
            onSaved?.(data);
            onClose?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="admin-edit-modal"
            className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="bg-[var(--bg-elevated)] border border-[var(--border-strong)] rounded-md w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 md:p-8 relative"
            >
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    data-testid="admin-edit-close"
                    className="absolute top-4 right-4 text-[var(--ink-muted)] hover:text-[var(--ink)]"
                >
                    <X size={18} strokeWidth={1.5} />
                </button>

                <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--accent)] mb-2">
                    {t("admin_edit.eyebrow")}
                </p>
                <h3
                    className="font-display text-2xl text-[var(--ink)] mb-6 break-words pr-6"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 88, "opsz" 36' }}
                >
                    {item?.title || item?.name || item?.id}
                </h3>

                <div className="space-y-4">
                    {fields.map((f) => (
                        <FieldRow
                            key={f.name}
                            field={f}
                            value={form[f.name] ?? ""}
                            onChange={(v) => setField(f.name, v)}
                            t={t}
                        />
                    ))}
                </div>

                <div className="flex items-center justify-end gap-3 mt-8">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="admin-edit-cancel"
                        className="text-xs uppercase tracking-widest text-[var(--ink-muted)] hover:text-[var(--ink)] px-4 py-2 transition-colors"
                    >
                        {t("admin_edit.cancel")}
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="admin-edit-save"
                        className="text-xs uppercase tracking-widest bg-[var(--ink)] text-[var(--bg)] hover:bg-[var(--accent)] transition-colors rounded-full px-5 py-2 disabled:opacity-50"
                    >
                        {saving ? t("admin_edit.saving") : t("admin_edit.save")}
                    </button>
                </div>
            </form>
        </div>
    );
}

function FieldRow({ field, value, onChange, t }) {
    const labelKey = `admin_edit.field.${field.name}`;
    const labelText = t(labelKey, { defaultValue: field.name });
    const baseClass = "w-full bg-transparent border border-[var(--border-strong)] rounded-md px-3 py-2 text-sm text-[var(--ink)] focus:outline-none focus:border-[var(--accent)]";
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-widest text-[var(--ink-muted)] mb-1.5">
                {labelText}
            </span>
            {field.type === "textarea" ? (
                <textarea
                    rows={3}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    className={baseClass}
                    data-testid={`admin-edit-input-${field.name}`}
                />
            ) : field.type === "select" ? (
                <select
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    className={baseClass}
                    data-testid={`admin-edit-input-${field.name}`}
                >
                    {field.options.map((o) => (
                        <option key={o} value={o}>{o}</option>
                    ))}
                </select>
            ) : (
                <input
                    type={field.type}
                    step={field.step}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    className={baseClass}
                    data-testid={`admin-edit-input-${field.name}`}
                />
            )}
        </label>
    );
}
